// /src/renderer/features/settings/tabs/settings/SettingsSetting.tsx
import React, { useEffect, useMemo, useState } from 'react';
import './SettingsSetting.scss';

import InputCustom from '@components/Input/InputCustom';
import Button from '@components/Button';
import Switch from '@components/Switch';
import Dropdown from '@components/Dropdown';

import { useConfig } from 'config/useConfig';
import type { AppConfig } from 'config/config';
import { CT_ANALYSIS_TYPE_NAMES } from 'config/labels';
import { configStore } from 'config/configStore';
import { useAuthPage } from '@features/auth/useAuthPage';
import type { AnalysisType } from 'config/analysisConfig';
import {
  isAnalysisTypeAvailableByServerState,
  useServerFeatureState,
} from 'config/serverFeatureState';

type KeysOfType<T> = {
  [K in keyof AppConfig]-?: AppConfig[K] extends T ? K : never;
}[keyof AppConfig];

type NumberKey = KeysOfType<number>;
type BooleanKey = KeysOfType<boolean>;
type StringKey = KeysOfType<string>;
type StringArrayKey = KeysOfType<string[]>;

type BaseField = {
  label: string;
  description?: string;
  span?: 1 | 2;
  group?: string;
};

type NumberField = BaseField & {
  kind: 'number';
  key: NumberKey;
  placeholder?: string;
  integer?: boolean;
  min?: number;
  max?: number;
};

type BooleanField = BaseField & {
  kind: 'boolean';
  key: BooleanKey;
};

type TextField = BaseField & {
  kind: 'text';
  key: StringKey;
  placeholder?: string;
  required?: boolean;
};

type OrderField<T extends readonly string[]> = BaseField & {
  kind: 'order';
  key: StringArrayKey;
  allowed: T;
  allowAdd?: boolean;
  allowRemove?: boolean;
  minItems?: number;
};

type ComboField<T extends readonly string[]> = BaseField & {
  kind: 'combo';
  key: StringKey;
  options: T;
  placeholder?: string;
};

/** ✅ modelCaptureVR 전용 커스텀 */
type CaptureVrItem = { capture: boolean; description: string };
type CaptureVrDraft = { order: string[]; value: Record<string, CaptureVrItem> };
type ModelCaptureVrKey = Extract<keyof AppConfig, 'modelCaptureVR'>;

type CaptureVrField<T extends readonly string[]> = BaseField & {
  kind: 'captureVr';
  key: ModelCaptureVrKey;
  allowed: T;
  allowAdd?: boolean;
  allowRemove?: boolean;
};

/** ✅ analysisParams 전용 커스텀 */
type AnalysisParamsKey = Extract<keyof AppConfig, 'analysisParams'>;

type AnalysisParamsDraftItem = {
  defaultWindowWidth: string;
  defaultWindowLevel: string;
  useDefaultWindow: boolean;
  isInverted: boolean; // optional이지만 UI에서는 토글로 관리
  enabled: boolean;
};

type AnalysisParamsDraft = {
  keys: string[];
  value: Record<string, AnalysisParamsDraftItem>;
};

type AnalysisParamsField = BaseField & {
  kind: 'analysisParams';
  key: AnalysisParamsKey;
};

/** ✅ volumeDilation 전용 커스텀 */
type VolumeDilationKey = Extract<keyof AppConfig, 'volumeDilation'>;
type VolumeDilationEnabledField = BaseField & {
  kind: 'volumeDilationEnabled';
  key: VolumeDilationKey;
};

type FieldDef =
  | NumberField
  | BooleanField
  | TextField
  | OrderField<readonly string[]>
  | CaptureVrField<readonly string[]>
  | ComboField<readonly string[]>
  | AnalysisParamsField
  | VolumeDilationEnabledField;

type SectionDef = {
  title: string;
  description?: string;
  fields: FieldDef[];
};

// ---------- field helper factories ----------
const numberField = <K extends NumberKey>(
  key: K,
  label: string,
  opt?: Omit<NumberField, 'kind' | 'key' | 'label'>,
): NumberField => ({
  kind: 'number',
  key,
  label,
  span: 1,
  ...opt,
});

const boolField = <K extends BooleanKey>(
  key: K,
  label: string,
  opt?: Omit<BooleanField, 'kind' | 'key' | 'label'>,
): BooleanField => ({
  kind: 'boolean',
  key,
  label,
  span: 1,
  ...opt,
});

const textField = <K extends StringKey>(
  key: K,
  label: string,
  opt?: Omit<TextField, 'kind' | 'key' | 'label'>,
): TextField => ({
  kind: 'text',
  key,
  label,
  span: 1,
  ...opt,
});

const comboField = <K extends StringKey, T extends readonly string[]>(
  key: K,
  label: string,
  options: T,
  opt?: Omit<ComboField<T>, 'kind' | 'key' | 'label' | 'options'>,
): ComboField<T> => ({
  kind: 'combo',
  key,
  label,
  options,
  span: 1,
  ...opt,
});

const orderField = <K extends StringArrayKey, T extends readonly string[]>(
  key: K,
  label: string,
  allowed: T,
  opt?: Omit<OrderField<T>, 'kind' | 'key' | 'label' | 'allowed'>,
): OrderField<T> => ({
  kind: 'order',
  key,
  label,
  allowed,
  span: 2,
  allowAdd: true,
  allowRemove: true,
  ...opt,
});

const captureVrField = <T extends readonly string[]>(
  key: ModelCaptureVrKey,
  label: string,
  allowed: T,
  opt?: Omit<CaptureVrField<T>, 'kind' | 'key' | 'label' | 'allowed'>,
): CaptureVrField<T> => ({
  kind: 'captureVr',
  key,
  label,
  allowed,
  span: 2,
  allowAdd: false,
  allowRemove: false,
  ...opt,
});

const analysisParamsField = (
  key: AnalysisParamsKey,
  label: string,
  opt?: Omit<AnalysisParamsField, 'kind' | 'key' | 'label'>,
): AnalysisParamsField => ({
  kind: 'analysisParams',
  key,
  label,
  span: 2,
  ...opt,
});

const volumeDilationEnabledField = (
  key: Extract<keyof AppConfig, 'volumeDilation'>,
  label: string,
  opt?: Omit<VolumeDilationEnabledField, 'kind' | 'key' | 'label'>,
): VolumeDilationEnabledField => ({
  kind: 'volumeDilationEnabled',
  key,
  label,
  span: 1,
  ...opt,
});

// ---------- utils ----------
function arrayMove<T>(arr: T[], from: number, to: number) {
  const next = [...arr];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

function arraysEqual(a: string[], b: string[]) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/**
 * value를 allowed 안에서만 정리 + 중복 제거
 * - fillMissing=true면 allowed에 있는 항목이 누락된 경우 자동으로 뒤에 채움(삭제 불가 모드)
 * - fillMissing=false면 삭제된 항목은 그대로 삭제 상태 유지
 */
function normalizeOrder(
  value: string[] | undefined,
  allowed: readonly string[],
  fillMissing: boolean,
) {
  const set = new Set(allowed);
  const out: string[] = [];
  for (const v of value ?? []) {
    if (!set.has(v)) continue;
    if (out.includes(v)) continue;
    out.push(v);
  }
  if (fillMissing) {
    for (const v of allowed) if (!out.includes(v)) out.push(v);
  }
  return out;
}

function validateNumber(
  raw: string,
  opt: { integer?: boolean; min?: number; max?: number } = {},
): { ok: true; value: number } | { ok: false; msg: string } {
  if (raw.trim() === '') return { ok: false, msg: '필수 값입니다.' };
  const n = Number(raw);
  if (!Number.isFinite(n)) return { ok: false, msg: '숫자를 입력하세요.' };
  if (opt.integer && !Number.isInteger(n))
    return { ok: false, msg: '정수만 입력하세요.' };
  if (typeof opt.min === 'number' && n < opt.min)
    return { ok: false, msg: `최소 ${opt.min}` };
  if (typeof opt.max === 'number' && n > opt.max)
    return { ok: false, msg: `최대 ${opt.max}` };
  return { ok: true, value: n };
}

// ---------- DnD Chips Editor ----------
function OrderDndChips(props: {
  label: string;
  description?: string;
  value: string[];
  allowed: readonly string[];
  allowAdd?: boolean;
  allowRemove?: boolean;
  minItems?: number;
  onChange: (next: string[]) => void;
  onError?: (msg?: string) => void;
}) {
  const {
    label,
    description,
    value,
    allowed,
    allowAdd = true,
    allowRemove = true,
    minItems,
    onChange,
    onError,
  } = props;

  const selected = useMemo(
    () => normalizeOrder(value, allowed, !allowRemove),
    [value, allowed, allowRemove],
  );
  const available = useMemo(
    () => allowed.filter((x) => !selected.includes(x)),
    [allowed, selected],
  );

  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  const remove = (item: string) => {
    if (!allowRemove) return;
    const next = selected.filter((x) => x !== item);
    if (typeof minItems === 'number' && next.length < minItems) {
      onError?.(`최소 ${minItems}개는 유지해야 합니다.`);
      return;
    }
    onError?.(undefined);
    onChange(next);
  };

  const add = (item: string) => {
    if (!allowAdd) return;
    onError?.(undefined);
    onChange([...selected, item]);
  };

  const onDragStart = (e: React.DragEvent<HTMLButtonElement>, idx: number) => {
    setDraggingIndex(idx);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(idx));
  };

  const onDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    setOverIndex(idx);
  };

  const onDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    const from = Number(e.dataTransfer.getData('text/plain'));
    if (!Number.isFinite(from)) return;

    const clamped = Math.max(0, Math.min(dropIndex, selected.length - 1));
    if (from === clamped) {
      setDraggingIndex(null);
      setOverIndex(null);
      return;
    }
    onChange(arrayMove(selected, from, clamped));
    setDraggingIndex(null);
    setOverIndex(null);
  };

  return (
    <div className="settings-order-dnd">
      <div className="settings-order-dnd-title">
        <div className="settings-order-dnd-label">{label}</div>
        {description && (
          <div className="settings-order-dnd-desc">{description}</div>
        )}
      </div>

      <div className="settings-chip-row">
        {selected.map((item, idx) => (
          <button
            key={item}
            type="button"
            className={[
              'settings-chip',
              draggingIndex === idx ? 'is-dragging' : '',
              overIndex === idx ? 'is-over' : '',
            ].join(' ')}
            draggable
            onDragStart={(e) => onDragStart(e, idx)}
            onDragOver={(e) => onDragOver(e, idx)}
            onDrop={(e) => onDrop(e, idx)}
            onDragEnd={() => {
              setDraggingIndex(null);
              setOverIndex(null);
            }}
          >
            <span className="settings-chip-text">{item}</span>

            {allowRemove && (
              <span
                className="settings-chip-remove"
                onClick={(e) => {
                  e.stopPropagation();
                  remove(item);
                }}
                role="button"
                aria-label={`Remove ${item}`}
              >
                ×
              </span>
            )}
          </button>
        ))}
      </div>

      {allowAdd && available.length > 0 && (
        <div className="settings-chip-add-row">
          <div className="settings-chip-add-label">Add</div>
          <div className="settings-chip-row">
            {available.map((item) => (
              <button
                key={item}
                type="button"
                className="settings-chip is-available"
                onClick={() => add(item)}
              >
                <span className="settings-chip-text">{item}</span>
                <span className="settings-chip-plus">+</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- modelCaptureVR Editor ----------
function CaptureVrEditor(props: {
  label: string;
  description?: string;
  allowed: readonly string[];
  value: CaptureVrDraft;
  allowAdd?: boolean;
  allowRemove?: boolean;
  onChange: (next: CaptureVrDraft) => void;
}) {
  const {
    label,
    description,
    allowed,
    value,
    allowAdd = false,
    allowRemove = false,
    onChange,
  } = props;

  const fillMissing = !allowRemove;
  const order = useMemo(
    () => normalizeOrder(value.order, allowed, fillMissing),
    [value.order, allowed, fillMissing],
  );

  const available = useMemo(
    () => allowed.filter((k) => !order.includes(k)),
    [allowed, order],
  );

  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  const setCapture = (key: string, capture: boolean) => {
    onChange({
      ...value,
      value: {
        ...value.value,
        [key]: {
          capture,
          description: String(value.value[key]?.description ?? key),
        },
      },
    });
  };

  const setDescription = (key: string, desc: string) => {
    onChange({
      ...value,
      value: {
        ...value.value,
        [key]: {
          capture: Boolean(value.value[key]?.capture),
          description: desc,
        },
      },
    });
  };

  const removeKey = (key: string) => {
    if (!allowRemove) return;
    onChange({ ...value, order: order.filter((x) => x !== key) });
  };

  const addKey = (key: string) => {
    if (!allowAdd) return;
    onChange({ ...value, order: [...order, key] });
  };

  const onDragStart = (e: React.DragEvent<HTMLButtonElement>, idx: number) => {
    setDragFrom(idx);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(idx));
  };

  const onDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    setOverIndex(idx);
  };

  const onDrop = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    const from = Number(e.dataTransfer.getData('text/plain'));
    if (!Number.isFinite(from)) return;

    const clamped = Math.max(0, Math.min(idx, order.length - 1));
    if (from === clamped) {
      setDragFrom(null);
      setOverIndex(null);
      return;
    }
    onChange({ ...value, order: arrayMove(order, from, clamped) });
    setDragFrom(null);
    setOverIndex(null);
  };

  return (
    <div className="settings-capturevr">
      <div className="settings-capturevr-title">
        <div className="settings-capturevr-label">{label}</div>
        {description && (
          <div className="settings-capturevr-desc">{description}</div>
        )}
      </div>

      <div className="settings-capturevr-list">
        {order.map((key, idx) => {
          const item = value.value[key] ?? { capture: false, description: key };
          const isOver = overIndex === idx;

          return (
            <div
              key={key}
              className={[
                'settings-capturevr-row',
                dragFrom === idx ? 'is-dragging' : '',
                isOver ? 'is-over' : '',
              ].join(' ')}
              onDragOver={(e) => onDragOver(e, idx)}
              onDrop={(e) => onDrop(e, idx)}
            >
              <div className="settings-capturevr-key">
                <button
                  type="button"
                  className="settings-capturevr-handle"
                  draggable={true}
                  onDragStart={(e) => onDragStart(e, idx)}
                  onDragEnd={() => {
                    setDragFrom(null);
                    setOverIndex(null);
                  }}
                >
                  ⋮⋮
                </button>

                <div className="settings-capturevr-keytext">{key}</div>

                {allowRemove && (
                  <button
                    type="button"
                    className="settings-capturevr-remove"
                    onClick={() => removeKey(key)}
                  >
                    ×
                  </button>
                )}
              </div>

              <div className="settings-capturevr-col settings-capturevr-col--capture">
                <div className="settings-capturevr-col-label">Capture</div>
                <Switch
                  checked={Boolean(item.capture)}
                  onChange={(v: boolean) => setCapture(key, v)}
                />
              </div>

              <div className="settings-capturevr-col settings-capturevr-col--desc">
                <div className="settings-capturevr-col-label">Description</div>
                <input
                  className="settings-capturevr-input"
                  value={String(item.description ?? '')}
                  onChange={(e) => setDescription(key, e.target.value)}
                  placeholder="description"
                />
              </div>
            </div>
          );
        })}
      </div>

      {allowAdd && available.length > 0 && (
        <div className="settings-capturevr-add-row">
          <div className="settings-capturevr-add-label">Add</div>
          <div className="settings-capturevr-chip-row">
            {available.map((k) => (
              <button
                key={k}
                type="button"
                className="settings-capturevr-chip"
                onClick={() => addKey(k)}
              >
                {k} <span className="settings-capturevr-plus">+</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- analysisParams Editor ----------
type AnalysisTypeKey = keyof typeof CT_ANALYSIS_TYPE_NAMES;

const getTypeLabel = (k: string) =>
  CT_ANALYSIS_TYPE_NAMES[k as AnalysisTypeKey] ?? k;
const ENABLED_ANALYSIS_PARAM_KEYS = Object.keys(CT_ANALYSIS_TYPE_NAMES).sort(
  (a, b) => a.localeCompare(b),
);
const FIXED_FIRST_ANALYSIS_PARAM_KEY = 'preview';

function normalizeAnalysisParamKeys(value: string[] | undefined) {
  const enabled = new Set(ENABLED_ANALYSIS_PARAM_KEYS);
  const out: string[] = [];

  if (enabled.has(FIXED_FIRST_ANALYSIS_PARAM_KEY)) {
    out.push(FIXED_FIRST_ANALYSIS_PARAM_KEY);
  }

  for (const key of value ?? []) {
    if (!enabled.has(key)) continue;
    if (out.includes(key)) continue;
    out.push(key);
  }

  for (const key of ENABLED_ANALYSIS_PARAM_KEYS) {
    if (!out.includes(key)) out.push(key);
  }

  return out;
}

function mergeHiddenAnalysisParamKeysAtEnd(
  visibleKeys: string[],
  allKeys: string[],
) {
  const visibleSet = new Set(visibleKeys);
  const hiddenKeys = allKeys.filter((key) => !visibleSet.has(key));
  return [...visibleKeys, ...hiddenKeys];
}

// function getAnalysisParamsOrderDiff(
//   before: Record<string, any>,
//   after: Record<string, any>,
// ) {
//   const beforeOrder = normalizeAnalysisParamKeys(Object.keys(before));
//   const afterOrder = normalizeAnalysisParamKeys(Object.keys(after));

//   return {
//     beforeOrder,
//     afterOrder,
//     orderChanged: !arraysEqual(beforeOrder, afterOrder),
//   };
// }

function AnalysisParamsEditor(props: {
  label: string;
  description?: string;
  value: AnalysisParamsDraft;
  onChange: (next: AnalysisParamsDraft) => void;
}) {
  const { label, description, value, onChange } = props;
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const { accessToken } = useAuthPage();
  const serverFeatureState = useServerFeatureState(accessToken);

  const setNum = (
    key: string,
    field: 'defaultWindowLevel' | 'defaultWindowWidth',
    v: string,
  ) => {
    const numericValue = v.replace(/[^0-9]/g, '');
    onChange({
      ...value,
      value: {
        ...value.value,
        [key]: {
          ...value.value[key],
          [field]: numericValue,
        },
      },
    });
  };

  const setBool = (
    key: string,
    field: 'useDefaultWindow' | 'isInverted' | 'enabled',
    v: boolean,
  ) => {
    onChange({
      ...value,
      value: {
        ...value.value,
        [key]: {
          ...value.value[key],
          [field]: v,
        },
      },
    });
  };

  const allKeys = useMemo(
    () => normalizeAnalysisParamKeys(value.keys),
    [value.keys],
  );

  const keys = useMemo(
    () =>
      allKeys.filter((key) =>
        isAnalysisTypeAvailableByServerState(
          key as AnalysisType,
          serverFeatureState,
        ),
      ),
    [allKeys, serverFeatureState],
  );

  const onDragStart = (e: React.DragEvent<HTMLButtonElement>, idx: number) => {
    if (keys[idx] === FIXED_FIRST_ANALYSIS_PARAM_KEY) {
      e.preventDefault();
      return;
    }

    setDragFrom(idx);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(idx));
  };

  const onDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    setOverIndex(idx);
  };

  const onDrop = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    const from = Number(e.dataTransfer.getData('text/plain'));
    if (!Number.isFinite(from)) return;
    if (keys[from] === FIXED_FIRST_ANALYSIS_PARAM_KEY) return;

    const clamped = Math.max(1, Math.min(idx, keys.length - 1));
    if (from !== clamped) {
      const nextVisibleKeys = arrayMove(keys, from, clamped);
      onChange({
        ...value,
        keys: mergeHiddenAnalysisParamKeysAtEnd(nextVisibleKeys, allKeys),
      });
    }

    setDragFrom(null);
    setOverIndex(null);
  };

  return (
    <div className="settings-analysisparams">
      <div className="settings-analysisparams-title">
        <div className="settings-analysisparams-label">{label}</div>
        {description && (
          <div className="settings-analysisparams-desc">{description}</div>
        )}
      </div>

      <div className="settings-analysisparams-header">
        <div className="settings-analysisparams-hcol settings-analysisparams-hcol--type">
          Menu
        </div>
        <div className="settings-analysisparams-hcol">Visible</div>
        <div className="settings-analysisparams-hcol">Window Level</div>
        <div className="settings-analysisparams-hcol">Window Width</div>
        <div className="settings-analysisparams-hcol">Default</div>
        <div className="settings-analysisparams-hcol">Invert</div>
      </div>

      <div className="settings-analysisparams-list">
        {keys.map((k, idx) => {
          const isFixedFirst = k === FIXED_FIRST_ANALYSIS_PARAM_KEY;
          const row =
            value.value?.[k] ??
            ({
              defaultWindowLevel: '0',
              defaultWindowWidth: '0',
              useDefaultWindow: false,
              isInverted: false,
              enabled: true,
            } as AnalysisParamsDraftItem);

          return (
            <div
              key={k}
              className={[
                'settings-analysisparams-row',
                dragFrom === idx ? 'is-dragging' : '',
                overIndex === idx ? 'is-over' : '',
                isFixedFirst ? 'is-fixed' : '',
              ].join(' ')}
              onDragOver={(e) => onDragOver(e, idx)}
              onDrop={(e) => onDrop(e, idx)}
            >
              <div className="settings-analysisparams-type" title={k}>
                <button
                  type="button"
                  className="settings-analysisparams-handle"
                  draggable={!isFixedFirst}
                  disabled={isFixedFirst}
                  onDragStart={(e) => onDragStart(e, idx)}
                  onDragEnd={() => {
                    setDragFrom(null);
                    setOverIndex(null);
                  }}
                  aria-label={`Move ${getTypeLabel(k)}`}
                >
                  ::
                </button>
                <span className="settings-analysisparams-type-text">
                  {getTypeLabel(k)}
                </span>
              </div>

              <div className="settings-analysisparams-cell settings-analysisparams-cell--toggle">
                {!isFixedFirst && (
                  <Switch
                    checked={Boolean(row.enabled ?? true)}
                    onChange={(v: boolean) => setBool(k, 'enabled', v)}
                  />
                )}
              </div>

              <div className="settings-analysisparams-cell">
                <input
                  className="settings-analysisparams-input"
                  value={row.defaultWindowLevel}
                  onChange={(e) =>
                    setNum(k, 'defaultWindowLevel', e.target.value)
                  }
                  placeholder="0"
                  inputMode="numeric"
                />
              </div>

              <div className="settings-analysisparams-cell">
                <input
                  className="settings-analysisparams-input"
                  value={row.defaultWindowWidth}
                  onChange={(e) =>
                    setNum(k, 'defaultWindowWidth', e.target.value)
                  }
                  placeholder="0"
                  inputMode="numeric"
                />
              </div>

              <div className="settings-analysisparams-cell settings-analysisparams-cell--toggle">
                <Switch
                  checked={Boolean(row.useDefaultWindow)}
                  onChange={(v: boolean) => setBool(k, 'useDefaultWindow', v)}
                />
              </div>

              <div className="settings-analysisparams-cell settings-analysisparams-cell--toggle">
                <Switch
                  checked={Boolean(row.isInverted)}
                  onChange={(v: boolean) => setBool(k, 'isInverted', v)}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------- Example schema ----------
const MAIN_BRANCHES = configStore.get().mainBranchOrder;
const CAPTURE_CHAMBER = configStore.get().chamberCaptureOrder;

const modelCaptureVR = configStore.get().modelCaptureVR;
const MODEL_CAPTURE_VR_KEYS = Object.keys(
  modelCaptureVR
) as Array<keyof typeof modelCaptureVR>;

const MIP_TYPES = ['AortaCoronary', 'AortaCoronaryWall', 'All', 'Ventricle'] as const;
const VR_TYPES = ['warm', 'red', 'orangeRed'] as const;
const CPR_GRAPH_TYPES = ['diameter', 'area', 'none'] as const;
const CPR_SERIES_MODES = ['combined', 'splitCpr', 'splitAll'] as const;
const CPR_SERIES_ORDER = configStore.get().cprSeriesOrder;

const SETTINGS_FIELD_GROUPS: Record<
  string,
  { title: string; description?: string }
> = {
  capture: {
    title: 'Capture Defaults',
    description: 'Angle, count, image size, and default chamber capture values.',
  },
  output: {
    title: 'Send / CPR Options',
    description: 'Send mode, CPR graph/series mode, and CPR-related toggles.',
  },
  model: {
    title: '3D / MIP Options',
    description: 'VR/MIP rendering options and model capture settings.',
  },
  order: {
    title: 'Order Settings',
    description: 'Drag to change branch, chamber, and CPR capture order.',
  },
};

const SCHEMA: SectionDef[] = [
  {
    title: 'Setting',
    fields: [
      numberField('stretchedAngle', 'Stretched Angle', { group: 'capture' }),
      numberField('stretchedCount', 'Stretched Count', {
        integer: true,
        group: 'capture',
      }),
      numberField('straightenedAngle', 'Straightened Angle', {
        group: 'capture',
      }),
      numberField('straightenedCount', 'Straightened Count', {
        integer: true,
        group: 'capture',
      }),

      numberField('captureWidth', 'Capture Width', {
        integer: true,
        group: 'capture',
      }),
      numberField('captureHeight', 'Capture Height', {
        integer: true,
        group: 'capture',
      }),
      numberField('captureCPRWidth', 'Capture CPR Width', {
        integer: true,
        group: 'capture',
      }),
      numberField('uploadWidth', 'Upload Width', {
        integer: true,
        group: 'capture',
      }),
      numberField('uploadHeight', 'Upload Height', {
        integer: true,
        group: 'capture',
      }),

      numberField('modelCaptureAngleH', 'Model Horizontal Angle', {
        group: 'capture',
      }),
      numberField('modelCaptureAngleV', 'Model Vertical Angle', {
        group: 'capture',
      }),
      numberField('modelCaptureCountH', 'Model Horizontal Count', {
        integer: true,
        group: 'capture',
      }),
      numberField('modelCaptureCountV', 'Model Vertical Count', {
        integer: true,
        group: 'capture',
      }),

      numberField(
        'chamberCaptureDefaultThickness',
        'Default Chamber Thickness',
        {
          group: 'capture',
        },
      ),
      numberField('chamberCaptureDefaultSpacing', 'Default Chamber Spacing', {
        group: 'capture',
      }),
      // numberField(
      //   'chamberCaptureDefaultWindowLevel',
      //   'Default Chamber Window Level',
      //   { integer: true },
      // ),
      // numberField(
      //   'chamberCaptureDefaultWindowWidth',
      //   'Default Chamber Window Width',
      //   { integer: true },
      // ),

      textField('sendmode', 'Send mode', { group: 'output' }),
      comboField('cprGraphType', 'CPR Graph Type', CPR_GRAPH_TYPES, {
        placeholder: 'Select graph type',
        group: 'output',
      }),
      comboField('cprSeriesMode', 'CPR Series Mode', CPR_SERIES_MODES, {
        placeholder: 'Select series mode',
        group: 'output',
      }),
      boolField('cprGroupByMainBranch', 'CPR Full Capture Group', {
        group: 'output',
      }),
      boolField('cprSplitIncludeModelStretched', 'Include Model In Stretched', {
        group: 'output',
      }),
      boolField(
        'cprSplitIncludeModelStraightened',
        'Include Model In Straightened',
        { group: 'output' },
      ),
      comboField('vrType', 'VR type', VR_TYPES, {
        placeholder: 'Select vrType',
        group: 'model',
      }),
      comboField('renderingModeMIP', 'MIP Mode', MIP_TYPES, {
        placeholder: 'Select renderingMode MIP',
        group: 'model',
      }),
      // volumeDilationEnabledField('volumeDilation', 'VR Dilation', {
      //   group: 'model',
      // }),

      boolField('modelCaptureFit', 'Model Capture Fit', { group: 'model' }),
      captureVrField(
        'modelCaptureVR',
        'Model Capture Order',
        MODEL_CAPTURE_VR_KEYS,
        {
          span: 2,
          allowAdd: false,
          allowRemove: false,
          group: 'model',
        },
      ),

      orderField('mainBranchOrder', 'Main Branch Order', MAIN_BRANCHES, {
        span: 2,
        allowAdd: true,
        allowRemove: true,
        minItems: 1,
        group: 'order',
      }),

      orderField(
        'chamberCaptureOrder',
        'Chamber Capture Order',
        CAPTURE_CHAMBER,
        {
          span: 2,
          allowAdd: true,
          allowRemove: true,
          minItems: 1,
          group: 'order',
        },
      ),
      orderField('cprSeriesOrder', 'CPR Series Order', CPR_SERIES_ORDER, {
        span: 2,
        allowAdd: false,
        allowRemove: false,
        minItems: 3,
        group: 'order',
      }),

      analysisParamsField('analysisParams', 'Menu Control', {
        span: 2,
      }),
    ],
  },
];

// ---------- Draft types ----------
type DraftValues = Partial<Record<NumberKey, string>> &
  Partial<Record<BooleanKey, boolean>> &
  Partial<Record<StringKey, string>> &
  Partial<Record<StringArrayKey, string[]>> &
  Partial<Record<ModelCaptureVrKey, CaptureVrDraft>> &
  Partial<Record<AnalysisParamsKey, AnalysisParamsDraft>> &
  Partial<Record<VolumeDilationKey, boolean>>;

type ErrorMap = Partial<Record<keyof AppConfig, string>>;

export default function SettingsSetting() {
  const { config, setData } = useConfig();

  const initDraft = () => {
    const d: DraftValues = {};

    for (const sec of SCHEMA) {
      for (const f of sec.fields) {
        if (f.kind === 'number') d[f.key] = String(config[f.key]);
        if (f.kind === 'boolean') d[f.key] = Boolean(config[f.key]);
        if (f.kind === 'text' || f.kind === 'combo')
          d[f.key] = String(config[f.key] ?? '');

        if (f.kind === 'order') {
          const fillMissing = !(f.allowRemove ?? true);
          d[f.key] = normalizeOrder(
            config[f.key] as string[],
            f.allowed,
            fillMissing,
          );
        }

        if (f.kind === 'captureVr') {
          const cfg =
            (config[f.key] as unknown as Record<string, CaptureVrItem>) ?? {};
          const fillMissing = !(f.allowRemove ?? true);

          const order = normalizeOrder(
            Object.keys(cfg),
            f.allowed,
            fillMissing,
          );

          const valueMap: Record<string, CaptureVrItem> = {};
          for (const k of f.allowed as readonly string[]) {
            valueMap[k] = {
              capture: Boolean(cfg?.[k]?.capture ?? false),
              description: String(cfg?.[k]?.description ?? k),
            };
          }

          d[f.key] = { order, value: valueMap };
        }

        if (f.kind === 'analysisParams') {
          const cfg =
            (config[f.key] as unknown as Record<
              string,
              {
                defaultWindowWidth: number;
                defaultWindowLevel: number;
                useDefaultWindow: boolean;
                isInverted?: boolean;
                enabled?: boolean;
              }
            >) ?? {};

          const keys = normalizeAnalysisParamKeys(Object.keys(cfg));

          const valueMap: Record<string, AnalysisParamsDraftItem> = {};
          for (const k of keys) {
            const it = cfg[k];
            valueMap[k] = {
              defaultWindowLevel: String(it?.defaultWindowLevel ?? 0),
              defaultWindowWidth: String(it?.defaultWindowWidth ?? 0),
              useDefaultWindow: Boolean(it?.useDefaultWindow ?? false),
              isInverted: Boolean(it?.isInverted ?? false),
              enabled:
                k === FIXED_FIRST_ANALYSIS_PARAM_KEY
                  ? true
                  : Boolean(it?.enabled ?? true),
            };
          }

          d[f.key] = { keys, value: valueMap };
        }

        if (f.kind === 'volumeDilationEnabled') {
          d[f.key] = Boolean(config[f.key]?.enabled);
        }
      }
    }

    return d;
  };

  const [draft, setDraft] = useState<DraftValues>(() => initDraft());
  const [dirty, setDirty] = useState(false);
  const [errors, setErrors] = useState<ErrorMap>({});

  useEffect(() => {
    if (!dirty) {
      setDraft(initDraft());
      setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, dirty]);

  const setDraftValue = <K extends keyof DraftValues>(
    key: K,
    value: DraftValues[K],
  ) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
    setErrors((prev) => ({
      ...prev,
      [key as unknown as keyof AppConfig]: undefined,
    }));
  };

  const computeIsSameAsConfig = useMemo(() => {
    for (const sec of SCHEMA) {
      for (const f of sec.fields) {
        if (f.kind === 'number') {
          const raw = String(draft[f.key] ?? String(config[f.key]));
          const r = validateNumber(raw, {
            integer: f.integer,
            min: f.min,
            max: f.max,
          });
          if (!r.ok) return false;
          if (r.value !== config[f.key]) return false;
        }

        if (f.kind === 'boolean') {
          const v = Boolean(draft[f.key] ?? config[f.key]);
          if (v !== config[f.key]) return false;
        }

        if (f.kind === 'text' || f.kind === 'combo') {
          const v = String(draft[f.key] ?? String(config[f.key] ?? ''));
          if (v !== String(config[f.key] ?? '')) return false;
        }

        if (f.kind === 'order') {
          const fillMissing = !(f.allowRemove ?? true);
          const v = normalizeOrder(
            (draft[f.key] as string[]) ?? (config[f.key] as string[]),
            f.allowed,
            fillMissing,
          );
          const c = normalizeOrder(
            config[f.key] as string[],
            f.allowed,
            fillMissing,
          );
          if (!arraysEqual(v, c)) return false;
        }

        if (f.kind === 'captureVr') {
          const fillMissing = !(f.allowRemove ?? true);
          const dvr = draft[f.key] as CaptureVrDraft | undefined;
          const cfg =
            (config[f.key] as unknown as Record<string, CaptureVrItem>) ?? {};

          if (!dvr) return false;

          const cfgOrder = normalizeOrder(
            Object.keys(cfg),
            f.allowed,
            fillMissing,
          );
          const dvrOrder = normalizeOrder(dvr.order, f.allowed, fillMissing);
          if (!arraysEqual(cfgOrder, dvrOrder)) return false;

          for (const k of f.allowed as readonly string[]) {
            const a = dvr.value[k];
            const b = cfg[k];
            if (Boolean(a?.capture) !== Boolean(b?.capture)) return false;
            if (String(a?.description ?? '') !== String(b?.description ?? ''))
              return false;
          }
        }

        if (f.kind === 'analysisParams') {
          const dval = draft[f.key] as AnalysisParamsDraft | undefined;
          const cfg =
            (config[f.key] as unknown as Record<
              string,
              {
                defaultWindowWidth: number;
                defaultWindowLevel: number;
                useDefaultWindow: boolean;
                isInverted?: boolean;
                enabled?: boolean;
              }
            >) ?? {};

          if (!dval) return false;

          const cfgKeys = normalizeAnalysisParamKeys(Object.keys(cfg));
          const dKeys = normalizeAnalysisParamKeys(dval.keys);
          if (!arraysEqual(cfgKeys, dKeys)) return false;

          for (const k of cfgKeys) {
            const c = cfg[k];
            const drow = dval.value?.[k];

            const wl = validateNumber(String(drow?.defaultWindowLevel ?? ''), {
              integer: true,
            });
            const ww = validateNumber(String(drow?.defaultWindowWidth ?? ''), {
              integer: true,
            });
            if (!wl.ok || !ww.ok) return false;

            if (wl.value !== Number(c?.defaultWindowLevel ?? 0)) return false;
            if (ww.value !== Number(c?.defaultWindowWidth ?? 0)) return false;

            if (
              Boolean(drow?.useDefaultWindow) !== Boolean(c?.useDefaultWindow)
            )
              return false;
            if (Boolean(drow?.isInverted) !== Boolean(c?.isInverted))
              return false;
            const cEnabled =
              k === FIXED_FIRST_ANALYSIS_PARAM_KEY
                ? true
                : Boolean(c?.enabled ?? true);
            if (Boolean(drow?.enabled ?? true) !== cEnabled) return false;
          }
        }
        if (f.kind === 'volumeDilationEnabled') {
          const v = Boolean(draft[f.key]);
          const c = Boolean(config[f.key]?.enabled);
          if (v !== c) return false;
        }
      }
    }
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, config]);

  const onReset = () => {
    setDraft(initDraft());
    setDirty(false);
    setErrors({});
  };

  const onSave = () => {
    const nextErrors: ErrorMap = {};
    const patch: Partial<AppConfig> = {};

    for (const sec of SCHEMA) {
      for (const f of sec.fields) {
        if (f.kind === 'number') {
          const raw = String(draft[f.key] ?? '');
          const r = validateNumber(raw, {
            integer: f.integer,
            min: f.min,
            max: f.max,
          });
          if (!r.ok) nextErrors[f.key] = r.msg;
          else (patch as any)[f.key] = r.value;
        }

        if (f.kind === 'boolean') {
          (patch as any)[f.key] = Boolean(draft[f.key]);
        }

        if (f.kind === 'text') {
          const v = String(draft[f.key] ?? '');
          if (f.required && v.trim() === '')
            nextErrors[f.key] = '필수 값입니다.';
          else (patch as any)[f.key] = v;
        }

        if (f.kind === 'combo') {
          const v = String(draft[f.key] ?? '');
          const opts = f.options as readonly string[];
          if (!opts.includes(v)) {
            nextErrors[f.key] = '목록에서 선택하세요.';
          } else {
            (patch as any)[f.key] = v;
          }
        }

        if (f.kind === 'order') {
          const fillMissing = !(f.allowRemove ?? true);
          const v = normalizeOrder(
            draft[f.key] as string[],
            f.allowed,
            fillMissing,
          );

          if (typeof f.minItems === 'number' && v.length < f.minItems) {
            nextErrors[f.key] = `최소 ${f.minItems}개는 유지해야 합니다.`;
          } else {
            (patch as any)[f.key] = v;
          }
        }

        if (f.kind === 'captureVr') {
          const dvr = draft[f.key] as CaptureVrDraft | undefined;
          if (!dvr) {
            nextErrors[f.key] = '값이 비어있습니다.';
          } else {
            const fillMissing = !(f.allowRemove ?? true);
            const orderedKeys = normalizeOrder(
              dvr.order,
              f.allowed,
              fillMissing,
            );

            const out: Record<string, CaptureVrItem> = {};
            for (const k of orderedKeys) {
              const item = dvr.value[k] ?? { capture: false, description: k };
              out[k] = {
                capture: Boolean(item.capture),
                description: String(item.description ?? ''),
              };
            }

            (patch as any)[f.key] = out;
          }
        }

        if (f.kind === 'analysisParams') {
          const dval = draft[f.key] as AnalysisParamsDraft | undefined;
          const cfg =
            (config[f.key] as unknown as Record<
              string,
              {
                defaultWindowWidth: number;
                defaultWindowLevel: number;
                useDefaultWindow: boolean;
                isInverted?: boolean;
                enabled?: boolean;
              }
            >) ?? {};

          if (!dval) {
            nextErrors[f.key] = '값이 비어있습니다.';
          } else {
            const out: Record<string, any> = {};
            const keys = normalizeAnalysisParamKeys(dval.keys);

            for (const k of keys) {
              const row = dval.value?.[k];
              const wl = validateNumber(String(row?.defaultWindowLevel ?? ''), {
                integer: true,
              });
              const ww = validateNumber(String(row?.defaultWindowWidth ?? ''), {
                integer: true,
              });

              if (!wl.ok || !ww.ok) {
                nextErrors[f.key] =
                  `analysisParams(${k}) 숫자 값을 확인하세요.`;
                break;
              }

              out[k] = {
                defaultWindowLevel: wl.value,
                defaultWindowWidth: ww.value,
                useDefaultWindow: Boolean(row?.useDefaultWindow),
                isInverted: Boolean(row?.isInverted),
                enabled:
                  k === FIXED_FIRST_ANALYSIS_PARAM_KEY
                    ? true
                    : Boolean(row?.enabled ?? true),
              };
            }

            if (!nextErrors[f.key]) {
              for (const [key, item] of Object.entries(cfg)) {
                if (!(key in out)) out[key] = item;
              }
              (patch as any)[f.key] = out;
            }
          }
        }

        if (f.kind === 'volumeDilationEnabled') {
          (patch as any)[f.key] = {
            ...config[f.key],
            enabled: Boolean(draft[f.key]),
          };
        }
      }
    }

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    // if (patch.analysisParams) {
    //   const diff = getAnalysisParamsOrderDiff(
    //     (config.analysisParams as unknown as Record<string, any>) ?? {},
    //     patch.analysisParams as unknown as Record<string, any>,
    //   );

    //   const orderChanged = diff.orderChanged;
    //   if (orderChanged) {
    //     console.log('[SettingsSetting] analysisParams order changed', diff);
    //   }
    // }
    setData(patch);
    setDirty(false);
    setErrors({});
  };

  const renderField = (f: FieldDef) => {
    const err = errors[f.key as keyof AppConfig];

    if (f.kind === 'number') {
      return (
        <div
          className="settings-field"
          style={{ gridColumn: `span ${f.span ?? 1}` }}
        >
          <InputCustom
            label={f.label}
            placeholder={f.placeholder}
            mode="number"
            value={String(draft[f.key] ?? '')}
            onChange={(v) => setDraftValue(f.key, v)}
            error={Boolean(err)}
          />
          {f.description && (
            <div className="settings-field-desc">{f.description}</div>
          )}
          {err && <div className="settings-field-error">{err}</div>}
        </div>
      );
    }

    if (f.kind === 'boolean') {
      const checked = Boolean(draft[f.key] ?? config[f.key]);

      return (
        <div
          className="settings-field settings-field--switch"
          style={{ gridColumn: `span ${f.span ?? 1}` }}
        >
          <div className="settings-switch-label">{f.label}</div>
          <Switch
            checked={checked}
            onChange={(v: boolean) => setDraftValue(f.key, v)}
          />
          {f.description && (
            <div className="settings-field-desc">{f.description}</div>
          )}
          {err && <div className="settings-field-error">{err}</div>}
        </div>
      );
    }

    if (f.kind === 'text') {
      return (
        <div
          className="settings-field"
          style={{ gridColumn: `span ${f.span ?? 1}` }}
        >
          <InputCustom
            label={f.label}
            placeholder={f.placeholder}
            mode="default"
            value={String(draft[f.key] ?? '')}
            onChange={(v) => setDraftValue(f.key, v)}
            error={Boolean(err)}
          />
          {f.description && (
            <div className="settings-field-desc">{f.description}</div>
          )}
          {err && <div className="settings-field-error">{err}</div>}
        </div>
      );
    }

    if (f.kind === 'combo') {
      const value = String(draft[f.key] ?? String(config[f.key] ?? ''));
      const labelId = `settings-label-${String(f.key)}`;

      return (
        <div
          className="settings-field"
          style={{ gridColumn: `span ${f.span ?? 1}` }}
        >
          <div id={labelId} className="settings-field-label">
            {f.label}
          </div>

          <Dropdown
            className="settings-combo-dropdown"
            value={value}
            options={f.options.map((x) => ({ value: x, label: x }))}
            placeholder={f.placeholder}
            onChange={(v) => setDraftValue(f.key, v)}
          />

          {f.description && (
            <div className="settings-field-desc">{f.description}</div>
          )}
          {err && <div className="settings-field-error">{err}</div>}
        </div>
      );
    }

    if (f.kind === 'order') {
      const fillMissing = !(f.allowRemove ?? true);
      const order = normalizeOrder(
        (draft[f.key] as string[]) ?? (config[f.key] as string[]),
        f.allowed,
        fillMissing,
      );

      return (
        <div className="settings-field" style={{ gridColumn: `1 / -1` }}>
          <OrderDndChips
            label={f.label}
            description={f.description}
            value={order}
            allowed={f.allowed}
            allowAdd={f.allowAdd}
            allowRemove={f.allowRemove}
            minItems={f.minItems}
            onChange={(next) => setDraftValue(f.key, next)}
            onError={(msg) => setErrors((prev) => ({ ...prev, [f.key]: msg }))}
          />
          {err && <div className="settings-field-error">{err}</div>}
        </div>
      );
    }

    if (f.kind === 'analysisParams') {
      const dval =
        (draft[f.key] as AnalysisParamsDraft | undefined) ??
        ({
          keys: [],
          value: {},
        } as AnalysisParamsDraft);

      return (
        <div className="settings-field" style={{ gridColumn: `1 / -1` }}>
          <AnalysisParamsEditor
            label={f.label}
            description={f.description}
            value={dval}
            onChange={(next) => setDraftValue(f.key, next)}
          />
          {err && <div className="settings-field-error">{err}</div>}
        </div>
      );
    }

    if (f.kind === 'volumeDilationEnabled') {
      const checked = Boolean(draft[f.key] ?? config[f.key]?.enabled);
      return (
        <div
          className="settings-field settings-field--switch"
          style={{ gridColumn: `span ${f.span ?? 1}` }}
        >
          <div className="settings-switch-label">{f.label}</div>
          <Switch
            checked={checked}
            onChange={(v: boolean) => setDraftValue(f.key, v)}
          />
          {f.description && (
            <div className="settings-field-desc">{f.description}</div>
          )}
          {err && <div className="settings-field-error">{err}</div>}
        </div>
      );
    }
    // captureVr
    const dvr = (draft[f.key] as CaptureVrDraft | undefined) ?? {
      order: [],
      value: {},
    };

    return (
      <div className="settings-field" style={{ gridColumn: `1 / -1` }}>
        <CaptureVrEditor
          label={f.label}
          description={f.description}
          allowed={f.allowed as readonly string[]}
          value={dvr}
          allowAdd={f.allowAdd}
          allowRemove={f.allowRemove}
          onChange={(next) => setDraftValue(f.key, next)}
        />
        {err && <div className="settings-field-error">{err}</div>}
      </div>
    );
  };

  const renderFieldBlocks = (fields: FieldDef[]) => {
    const blocks: Array<{ group?: string; fields: FieldDef[] }> = [];

    for (const field of fields) {
      const last = blocks[blocks.length - 1];
      if (last && last.group === field.group) {
        last.fields.push(field);
      } else {
        blocks.push({ group: field.group, fields: [field] });
      }
    }

    return blocks.map((block, blockIndex) => {
      const content = (
        <div className="settings-grid">
          {block.fields.map((field) => (
            <React.Fragment key={String(field.key)}>
              {renderField(field)}
            </React.Fragment>
          ))}
        </div>
      );

      if (!block.group) {
        return (
          <div
            key={`plain-${blockIndex}`}
            className="settings-layout-plain-block"
          >
            {content}
          </div>
        );
      }

      const groupMeta = SETTINGS_FIELD_GROUPS[block.group] ?? {
        title: block.group,
      };

      return (
        <div key={block.group} className="settings-layout-group">
          <div className="settings-layout-group-header">
            <div className="settings-layout-group-title">{groupMeta.title}</div>
            {groupMeta.description && (
              <div className="settings-layout-group-desc">
                {groupMeta.description}
              </div>
            )}
          </div>
          {content}
        </div>
      );
    });
  };

  return (
    <div className="settings-page">
      {SCHEMA.map((sec) => (
        <div key={sec.title} className="settings-section">
          <div className="settings-section-titlebar">
            <div className="settings-section-titlebar-left">
              <div className="settings-section-title">{sec.title}</div>
              {sec.description && (
                <div className="settings-section-desc">{sec.description}</div>
              )}
            </div>

            <div className="settings-section-titlebar-actions">
              <Button disabled={!dirty} onClick={onReset}>
                Reset
              </Button>
              <Button
                className="action-control save"
                disabled={!dirty || computeIsSameAsConfig}
                onClick={onSave}
              >
                Save
              </Button>
            </div>
          </div>

          {/* <div className="settings-section-content">
            <div className="settings-grid">
              {sec.fields.map((f) => (
                <React.Fragment key={String(f.key)}>
                  {renderField(f)}
                </React.Fragment>
              ))}
            </div>
          </div> */}

          <div className="settings-section-content">
            <div className="settings-section-content-inner">
              <div className="settings-layout">
                {renderFieldBlocks(sec.fields)}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
